/**
 * The resend-confirmation dialog's state and its one write (BON-630) — split
 * from the dialog that draws it, same division as `useReservationContactForm`.
 *
 * THE RULE, different from that file's: NOT EVERY FAILURE STAYS IN THE FORM.
 * `classifyResendFailure` (`resend-confirmation-outcome.ts`) decides — a 502 send
 * failure or an unknown outcome closes the dialog behind a must-dismiss alert,
 * because there is nothing left to retype; a 400/403/404/409 (other than
 * `BOOKING_NOT_CONFIRMED`) means the server decided nothing, so the typed
 * address is kept and the error goes inline.
 */
import { useState } from 'react';

import { resendBookingConfirmation } from '../../api/booking-confirmation';
import type { BookingDetail } from '../../api/bookingsV2';

import {
  classifyResendFailure,
  resentOutcome,
  type ResendConfirmationOutcome,
} from './resend-confirmation-outcome';

export interface ResendConfirmationFormState {
  /** The address on file when the dialog opened. Never mutated by typing. */
  emailOnFile: string | null;
  /** True when there was no email on file — the correction field is then
   *  always open and required, with no "use the email on file" escape hatch. */
  forceCorrection: boolean;
  correcting: boolean;
  toggleCorrecting: () => void;
  email: string;
  setEmail: (v: string) => void;
  sending: boolean;
  error: string | null;
  /** True only for the local "the field is empty" refusal (BON-630 round 2) —
   *  drives `aria-invalid` + the red border on the email field. Narrower than
   *  "any error while correcting": a server refusal about the booking's
   *  STATE (e.g. `BOOKING_NOT_CONFIRMED`) is not a problem with what is typed
   *  in this field, so it must not paint the field invalid. */
  emailInvalid: boolean;
  submit: (e: React.FormEvent) => Promise<void>;
}

export function useResendConfirmationForm(
  booking: BookingDetail,
  /** Fired for a send (success or failure) that SETTLES the attempt — the
   *  caller closes the dialog and owns the toast, the alert and the refresh.
   *  Never fired for an inline refusal (see file docblock). */
  onDone: (outcome: ResendConfirmationOutcome) => void,
  /** Re-read the booking + Activity without closing the dialog — fired
   *  alongside an inline `BOOKING_NOT_CONFIRMED` refusal, whose booking is
   *  already stale by the time it reaches the CSR. */
  onRefresh: () => void,
): ResendConfirmationFormState {
  const emailOnFile = booking.customer.email;
  const forceCorrection = !emailOnFile;
  const [correcting, setCorrecting] = useState(forceCorrection);
  const [email, setEmail] = useState(emailOnFile ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailInvalid, setEmailInvalid] = useState(false);

  const toggleCorrecting = () => {
    if (forceCorrection) return;
    setCorrecting((c) => !c);
    setEmail(emailOnFile ?? '');
    setError(null);
    setEmailInvalid(false);
  };

  const setEmailAndClearError = (v: string) => {
    setEmail(v);
    if (error) setError(null);
    if (emailInvalid) setEmailInvalid(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (correcting && email.trim().length === 0) {
      setError('Enter an email address.');
      setEmailInvalid(true);
      return;
    }
    setSending(true);
    setError(null);
    setEmailInvalid(false);
    try {
      const trimmed = email.trim();
      // {} when the field is closed, or reopened onto an address that matches
      // the one on file — the server treats an equal (normalized) address as
      // no correction, but sending it anyway would still be a correction
      // *attempt*, which is not what an unchanged field means here.
      const body = correcting && trimmed !== (emailOnFile ?? '') ? { customerEmail: trimmed } : {};
      const result = await resendBookingConfirmation(booking.id, body);
      onDone(resentOutcome(result));
    } catch (err) {
      const classified = classifyResendFailure(err);
      if (classified.kind === 'settle') {
        onDone(classified.outcome);
        return;
      }
      setError(classified.message);
      setSending(false);
      if (classified.refresh) onRefresh();
    }
  };

  return {
    emailOnFile,
    forceCorrection,
    correcting,
    toggleCorrecting,
    email,
    setEmail: setEmailAndClearError,
    sending,
    error,
    emailInvalid,
    submit,
  };
}
